import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Icon, Message, Input, Modal } from 'semantic-ui-react';
import SuccessModal from '../../../components/SuccessModal';
import ErrorModal from '../../../components/ErrorModal';
import SubscriptionSelectionModal from '../../../components/SubscriptionSelectionModal';
import CreationLogsModal from '../../../components/CreationLogsModal';
import VMCreationErrorModal from '../../../components/VMCreationErrorModal';
import axios from 'axios';
import { useDropdownOptions } from '../../../hooks/useDropdownOptions';

const AzureForm = ({
    onSubmit,
    userEmail,
    roles,
    categories = [],
    linodeEnabled = false,
    initialValues = null,
    originalCloudName = null
}) => {
    const [formData, setFormData] = useState({
        tenant: '4', // Default to *.parablu.com
        location: '',
        vmSize: '2', // Default to 4vCPU-16GB RAM
        cloudName: '',
        storageDestination: '',
        category: '',
        diskSizeGB: '512', // Default to 512GB
        azureBlobHosted: false,
        azureBlobFuse: true, // Hidden but always true
        hasLicenseFile: false, // Default to yes
        licenseFile: null,
        tenantInfo: null, // Will contain subscription info based on tenant
        isMultiTenancySetup: false, // Default to Single Tenant Setup
        isMigrationSetup: false
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [allRequests, setAllRequests] = useState([]);
    const [requestError, setRequestError] = useState(null);
    const [formCategory, setFormCategory] = useState('');
    const [portalUrl, setPortalUrl] = useState('');
    const [availableLocations, setAvailableLocations] = useState([]);
    const [copySuccess, setCopySuccess] = useState(false);
    const isInitializingFromInitialValues = useRef(false);
    const [autoProvisionInfo, setAutoProvisionInfo] = useState({
        enabled: false,
        categories: []
    });
    const [showAutoProvisionVerifyModal, setShowAutoProvisionVerifyModal] = useState(false);
    const [pendingAutoProvisionData, setPendingAutoProvisionData] = useState(null);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [autoProvisionRequest, setAutoProvisionRequest] = useState(null);
    const [subscriptions, setSubscriptions] = useState([]);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [creationLogs, setCreationLogs] = useState([]);
    const [isCancelled, setIsCancelled] = useState(false);
    const [abortController, setAbortController] = useState(null);
    const [vmCreationError, setVMCreationError] = useState(null);
    const [showVMCreationErrorModal, setShowVMCreationErrorModal] = useState(false);

    const { options: tenantOptions } = useDropdownOptions('azure_tenants');
    const { options: allLocations } = useDropdownOptions('single_tenancy_locations');
    const { options: hostingTypeOptions } = useDropdownOptions('hosting_types');

    // Function to get the tenant URL placeholder based on selected tenant and location
    const getTenantUrlPlaceholder = () => {
        if (formData.tenant === '4') { // *.crashplan.com
            if (formData.location) {
                const selectedLocation = allLocations.find(loc => loc.value === formData.location);
                if (selectedLocation) {
                    return `https://${selectedLocation.urlPrefix}.crashplan.com/`;
                }
            }
            return 'https://{tenant-url}/';
        } else if (formData.tenant === '2') { // poc.crashplan.com
            return 'https://poc.crashplan.com/';
        } else if (formData.tenant === '3') { // demo.crashplan.com
            return 'https://demo.crashplan.com/';
        } else if (formData.tenant === '1' && formData.location) { // *.parablu.com with location
            const selectedLocation = allLocations.find(loc => loc.value === formData.location);
            if (selectedLocation) {
                return `https://${selectedLocation.urlPrefix}.parablu.com/`;
            }
        }

        // Default placeholder when tenant or location not selected
        return 'https://{tenant-url}/';
    };

    const resolveCategoryValue = (rawCategory) => {
        if (!rawCategory) return '';
        const normalized = rawCategory.toString().trim().toUpperCase();
        const exactMatch = categories.find(cat => cat === normalized);
        if (exactMatch) return exactMatch;
        const caseInsensitiveMatch = categories.find(
            cat => cat && cat.toString().trim().toUpperCase() === normalized
        );
        return caseInsensitiveMatch || normalized;
    };

    // Update available locations and add tenant-specific data when tenant or category changes
    useEffect(() => {
        // Reset location selection when tenant or category changes (but not when initializing from initialValues)
        // Only reset if we're not initializing from initialValues
        // In edit mode, keep existing values instead of clearing fields on every category/tenant change.
        if (!isInitializingFromInitialValues.current && !initialValues) {
            setFormData(prev => ({ ...prev, location: '', cloudName: '' }));
        }

        // Helper function to filter locations based on category and tenant
        const filterByCategoryAndTenant = (locations) => {
            return locations.filter(loc => {
                // Check tenant restrictions first
                if (loc.tenants && loc.tenants.length > 0) {
                    if (!loc.tenants.includes(formData.tenant)) {
                        return false; // Location not available for this tenant
                    }
                }

                // Check category restrictions
                if (loc.categories && loc.categories.length > 0) {
                    if (!formData.category) return false; // Category required but not selected
                    return loc.categories.includes(formData.category);
                }

                // If no restrictions, location is available
                return true;
            });
        };

        // Small helper to enforce Central India only for specific categories
        const applyCentralIndiaOnly = (locations) => {
            const centralIndia = locations.find(loc => loc.value === '1') || allLocations.find(loc => loc.value === '1');
            if (centralIndia) {
                setAvailableLocations([centralIndia]);
                setFormData(prev => ({
                    ...prev,
                    location: '1'
                }));
            } else {
                setAvailableLocations([]);
            }
        };

        const isEnggDevOrQa = formData.category === 'ENGG_DEV' || formData.category === 'ENGG_QA';

        // Filter locations based on selected tenant
        if (formData.tenant === '4') { // *.crashplan.com
            // All locations available for crashplan (except South East Asia)
            const crashplanLocations = allLocations.filter(loc =>
                loc.text !== 'South East Asia'
            );
            const filteredLocations = filterByCategoryAndTenant(crashplanLocations);

            if (isEnggDevOrQa) {
                applyCentralIndiaOnly(filteredLocations);
            } else {
                setAvailableLocations(filteredLocations);
            }

            // Add tenant-specific info
            setFormData(prev => ({
                ...prev,
                tenantInfo: {
                    subscriptionId: process.env.REACT_APP_CRASHPLAN_SUBSCRIPTION_ID,
                    subscriptionName: 'CrashPlan-Subscription'
                }
            }));
        } else if (formData.tenant === '1') { // *.parablu.com
            // All locations EXCEPT South East Asia
            const parabluLocations = allLocations.filter(loc =>
                loc.text !== 'South East Asia'
            );
            const filteredLocations = filterByCategoryAndTenant(parabluLocations);

            if (isEnggDevOrQa) {
                applyCentralIndiaOnly(filteredLocations);
            } else {
                setAvailableLocations(filteredLocations);
            }
        } else if (formData.tenant === '2') { // poc.crashplan.com
            // East US and South East Asia available
            const pocLocations = allLocations.filter(loc =>
                loc.text === 'East US' || loc.text === 'South East Asia'
            );
            const filteredLocations = filterByCategoryAndTenant(pocLocations);

            if (isEnggDevOrQa) {
                applyCentralIndiaOnly(filteredLocations);
            } else {
                setAvailableLocations(filteredLocations);
            }

            // Add tenant-specific info
            setFormData(prev => ({
                ...prev,
                tenantInfo: {
                    subscriptionId: process.env.REACT_APP_CPG_POC_SUBSCRIPTION_ID, // CPG-POC-Subscription
                    subscriptionName: 'CPG-POC-Subscription'
                }
            }));
        } else if (formData.tenant === '3') { // demo.crashplan.com
            // Only East US available
            const eastUSLocation = allLocations.filter(loc => loc.text === 'East US');
            const filteredLocations = filterByCategoryAndTenant(eastUSLocation);

            if (isEnggDevOrQa) {
                applyCentralIndiaOnly(filteredLocations);
            } else {
                setAvailableLocations(filteredLocations);
            }

            // Add tenant-specific info
            setFormData(prev => ({
                ...prev,
                tenantInfo: {
                    subscriptionId: process.env.REACT_APP_CPG_DEMO_SUBSCRIPTION_ID, // CPG-Demo-Subscription
                    subscriptionName: 'CPG-Demo-Subscription'
                }
            }));
        } else {
            // Default to all locations
            const filteredLocations = filterByCategoryAndTenant(allLocations);

            if (isEnggDevOrQa) {
                applyCentralIndiaOnly(filteredLocations);
            } else {
                setAvailableLocations(filteredLocations);
            }

            // Reset tenant info
            setFormData(prev => ({ ...prev, tenantInfo: null }));
        }
    }, [formData.tenant, formData.category, initialValues, allLocations]);

    // Update the portal URL whenever tenant, location or cloudName changes
    const cloudNameLower = formData.cloudName.toLowerCase();
    useEffect(() => {
        if (!cloudNameLower) {
            setPortalUrl('');
            return;
        }

        let baseUrl = '';
        let locationPrefix = '';

        // Determine base URL by tenant
        if (formData.tenant === '4') { // *.crashplan.com
            // For crashplan.com, determine URL by location
            if (formData.location) {
                const selectedLocation = allLocations.find(loc => loc.value === formData.location);
                if (selectedLocation) {
                    locationPrefix = selectedLocation.urlPrefix;
                    baseUrl = `https://${locationPrefix}.crashplan.com/`;
                }
            }
        } else if (formData.tenant === '2') { // poc.crashplan.com
            baseUrl = 'https://poc.crashplan.com/';
        } else if (formData.tenant === '3') { // demo.crashplan.com
            baseUrl = 'https://demo.crashplan.com/';
        } else { // *.parablu.com (default)
            // For parablu.com, determine URL by location
            if (formData.location) {
                const selectedLocation = allLocations.find(loc => loc.value === formData.location);
                if (selectedLocation) {
                    locationPrefix = selectedLocation.urlPrefix;
                    baseUrl = `https://${locationPrefix}.parablu.com/`;
                }
            }
        }

        if (baseUrl) {
            setPortalUrl(`${baseUrl}${cloudNameLower}/portal/`);
        } else {
            setPortalUrl('');
        }
    }, [formData.tenant, formData.location, cloudNameLower, allLocations]);

    // Fetch auto provisioning access for current user
    useEffect(() => {
        const fetchAutoProvisionInfo = async () => {
            if (!userEmail) return;
            try {
                const response = await axios.get('https://qa-tenantbridge.parablu.com/api/user/roles', {
                    withCredentials: true,
                    timeout: 5000
                });
                
                // Determine if this is a multi-tenancy setup
                // Handle boolean true, string 'true', and ensure empty string/false/null are treated as false
                const isMultiTenancy = formData.isMultiTenancySetup === true || 
                                      formData.isMultiTenancySetup === 'true' ||
                                      (typeof formData.isMultiTenancySetup === 'string' && formData.isMultiTenancySetup.toLowerCase() === 'true');
                
                // Use the appropriate tenancy type fields
                const enabled = isMultiTenancy 
                    ? !!response.data.autoProvisionMultiTenancyEnabled 
                    : !!response.data.autoProvisionSingleTenancyEnabled;
                const categories = isMultiTenancy 
                    ? (response.data.autoProvisionMultiTenancyCategories || [])
                    : (response.data.autoProvisionSingleTenancyCategories || []);
                
                console.log('Auto-provision info fetched:', {
                    enabled: enabled,
                    categories: categories,
                    isMultiTenancy: isMultiTenancy
                });
                
                setAutoProvisionInfo({
                    enabled: enabled,
                    categories: categories
                });
            } catch (err) {
                console.error('Error fetching auto-provision info:', err);
                // Fail silently; auto provisioning is an enhancement
                setAutoProvisionInfo({
                    enabled: false,
                    categories: []
                });
            }
        };

        fetchAutoProvisionInfo();
    }, [userEmail, formData.isMultiTenancySetup]);

    const hasAutoProvisionForSelectedCategory =
        autoProvisionInfo.enabled &&
        formData.category &&
        Array.isArray(autoProvisionInfo.categories) &&
        autoProvisionInfo.categories.some(
            (c) =>
                c &&
                c.trim().toUpperCase() === formData.category.trim().toUpperCase()
        );

    // Debug logging
    useEffect(() => {
        if (formData.category) {
            console.log('Category selected:', formData.category);
            console.log('Auto-provision enabled:', autoProvisionInfo.enabled);
            console.log('Auto-provision categories:', autoProvisionInfo.categories);
            console.log('Has auto-provision for category:', hasAutoProvisionForSelectedCategory);
        }
    }, [formData.category, autoProvisionInfo.enabled, autoProvisionInfo.categories, hasAutoProvisionForSelectedCategory]);

    const { options: storageOptions } = useDropdownOptions('single_tenancy_storage_destinations');

    // Transform categories into dropdown options
    const categoryOptions = categories.map(category => ({
        key: category,
        text: category,
        value: category
    }));

    const validateField = (name, value) => {
        switch (name) {
            case 'tenant':
                return !value ? 'Tenant is required' : '';

            case 'location':
                return !value ? 'Location is required' : '';

            case 'cloudName':
                if (!value) return 'Cloud name is required';
                if (value.length < 3) return 'Cloud name must be at least 3 characters';
                if (value.length > 20) return 'Cloud name cannot exceed 20 characters';
                if (!/^[a-zA-Z0-9]+$/.test(value)) return 'Cloud name can only contain letters and numbers';

                // Skip duplicate validation if migration setup is enabled
                if (!formData.isMigrationSetup) {
                    const cloudNameError = validateCloudName(value);
                    if (cloudNameError) return cloudNameError;
                }
                return '';

            case 'storageDestination':
                return (!value && !formData.isMigrationSetup) ? 'Storage destination is required' : '';

            case 'category':
                return !value ? 'Category is required' : '';

            case 'licenseFile':
                if (formData.hasLicenseFile && !formData.isMigrationSetup) {
                    if (!value) return 'License key file is required';
                    if (!value.name.endsWith('.pblic')) return 'File must be a .pblic file';
                }
                return '';

            case 'isMigrationSetup':
                return value === '' || value === null || value === undefined ? 'Migration setup selection is required' : '';

            case 'isMultiTenancySetup':
                return value === '' ? 'Setup type is required' : '';

            default:
                return '';
        }
    };

    const validateCloudName = (cloudName) => {
        if (!cloudName) return '';
        if (!allRequests || allRequests.length === 0) return '';

        // Skip validation if migration setup is enabled
        if (formData.isMigrationSetup) return '';

        // Skip validation if editing the same request (cloud name hasn't changed)
        if (originalCloudName && cloudName.toLowerCase() === originalCloudName.toLowerCase()) {
            return '';
        }

        const existingRequest = allRequests.find(request => {
            const status = request?.status?.toLowerCase() || '';
            const isReusableStatus =
                status.includes('failed-vm creation') ||
                status.includes('withdrawn');

            return request?.formData?.cloudName?.toLowerCase() === cloudName.toLowerCase() &&
                !isReusableStatus;
        });

        if (existingRequest) {
            return `Cloud name "${cloudName}" already exists and is in ${existingRequest.status.toLowerCase()}`;
        }
        return '';
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e, { name, value }) => {
        if (name === 'storageDestination') {
            // Find the selected storage option
            const selectedOption = storageOptions.find(opt => opt.value === value);

            // Set azureBlobHosted automatically based on the selection
            setFormData(prev => ({
                ...prev,
                [name]: value,
                azureBlobHosted: selectedOption ? selectedOption.azureBlobHosted : false
            }));
        } else if (name === 'isMultiTenancySetup') {
            // Normalize isMultiTenancySetup to boolean to ensure consistent type
            const normalizedValue = value === true || value === 'true' || value === 1 || value === '1';
            setFormData(prev => ({ ...prev, [name]: normalizedValue }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (name === 'cloudName') {
            const error = validateField('cloudName', value);
            setErrors(prev => ({ ...prev, cloudName: error }));
        } else {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file && file.name.endsWith('.pblic')) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const base64Content = event.target.result;
                const fileData = {
                    name: file.name,
                    content: base64Content,
                    type: file.type
                };
                setFormData(prev => ({
                    ...prev,
                    licenseFile: fileData
                }));
            };
            reader.readAsDataURL(file);
            setErrors(prev => ({ ...prev, licenseFile: '' }));
        } else {
            setErrors(prev => ({
                ...prev,
                licenseFile: 'Please upload a valid .pblic file'
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }

        try {
            setSubmitting(true);
            setErrors({});

            // Map tenant values to backend tenant keys
            const tenantKeyMap = {
                '4': 'crashplan', // *.crashplan.com maps to 'crashplan' backend key
                '1': 'parablu',   // *.parablu.com maps to 'parablu' backend key
                '2': 'poc',       // poc.crashplan.com maps to 'poc' backend key
                '3': 'demo'       // demo.crashplan.com maps to 'demo' backend key
            };

            // Find the selected storage option to get the actual value
            const selectedTenant = tenantOptions.find(opt => opt.value === formData.tenant);
            const tenantKey = tenantKeyMap[formData.tenant];
            const selectedStorage = storageOptions.find(opt => opt.value === formData.storageDestination);

            const transformedData = {
                tenant: selectedTenant?.actual,
                location: allLocations.find(loc => loc.value === formData.location)?.details,
                vmSize: 'Standard_D4as_v5', // Hardcoded 4vCPU-16GB RAM
                cloudName: formData.cloudName.toLowerCase(),
                storageDestination: formData.isMigrationSetup ? "" : selectedStorage?.actual, // Send empty string for migration
                category: formData.category,
                diskSizeGB: 512, // Hardcoded 512GB
                azureBlobFuse: formData.isMigrationSetup ? false : formData.azureBlobFuse,
                azureBlobHosted: formData.isMigrationSetup ? false : formData.azureBlobHosted,
                submittedBy: userEmail,
                submittedAt: new Date().toISOString(),
                hasLicenseFile: formData.hasLicenseFile,
                licenseFile: formData.hasLicenseFile ? formData.licenseFile : null,
                expiryDays: null,
                azureTenantId: selectedTenant?.azureTenantId,
                subscriptionId: selectedTenant?.subscriptionId,
                tenantKey: tenantKey, // Add this key for backend mapping
                // Include subscription information for approval process
                subscriptionName: formData.tenantInfo?.subscriptionName,
                // Include portal URL
                portalUrl: portalUrl,
                // Add more detailed tenant information for Azure service initialization
                tenantInfo: {
                    id: selectedTenant?.azureTenantId,
                    name: selectedTenant?.text,
                    subscriptionId: selectedTenant?.subscriptionId,
                    subscriptionName: formData.tenantInfo?.subscriptionName || selectedTenant?.text
                },
                // NEW: Add Gmail/GDrive setup flag
                isMultiTenancySetup: formData.isMultiTenancySetup,
                isMigrationSetup: formData.isMigrationSetup,
                // Flag requests that should follow auto-provisioning flow for this submission
                autoProvision: hasAutoProvisionForSelectedCategory
            };

            // If auto provisioning applies, show verification modal first
            if (hasAutoProvisionForSelectedCategory) {
                setPendingAutoProvisionData(transformedData);
                setShowAutoProvisionVerifyModal(true);
                return;
            }

            // Normal submit flow
            setFormCategory(transformedData.category);
            const result = await onSubmit(transformedData);

            if (result.success) {
                setShowSuccessModal(true);
                setFormData({
                    tenant: '4', // Default to *.crashplan.com
                    location: '',
                    vmSize: '2',
                    cloudName: '',
                    storageDestination: '',
                    category: '',
                    diskSizeGB: '512',
                    azureBlobHosted: false,
                    azureBlobFuse: true,
                    hasLicenseFile: false,
                    licenseFile: null,
                    tenantInfo: null,
                    isMultiTenancySetup: false // Reset to default
                });
            } else {
                setSubmitError(result.error || 'Failed to submit request');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Submit error:', error);
            setSubmitError(error.message || 'An unexpected error occurred');
            setShowErrorModal(true);
        } finally {
            setSubmitting(false);
        }
    };

    const handleConfirmAutoProvisionSubmit = async () => {
        if (!pendingAutoProvisionData) return;
        try {
            setSubmitting(true);
            setShowAutoProvisionVerifyModal(false);
            setFormCategory(pendingAutoProvisionData.category);
            
            // Submit directly to API to get requestId
            const submitResponse = await axios.post('https://qa-tenantbridge.parablu.com/api/submit-request', {
                formData: {
                    ...pendingAutoProvisionData,
                    submittedBy: userEmail,
                    submittedAt: new Date().toISOString()
                }
            }, { withCredentials: true });

            const result = {
                success: submitResponse.data.success,
                message: submitResponse.data.message,
                requestId: submitResponse.data.requestId
            };

            console.log('Auto-provision submit result:', result);

            if (result.success && result.requestId) {
                // For auto-provision requests, fetch the request and show subscription modal
                // Wait a moment for the request to be saved in the database
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                try {
                    console.log('Fetching auto-provision request:', result.requestId);
                    // Fetch the auto-provision request
                    const response = await axios.get(
                        `https://qa-tenantbridge.parablu.com/api/auto-provision/request/${result.requestId}`,
                        { withCredentials: true }
                    );

                    console.log('Auto-provision request response:', response.data);

                    if (response.data.success && response.data.request) {
                        // Fetch subscriptions
                        const subsResponse = await axios.get(
                            'https://qa-tenantbridge.parablu.com/api/subscriptions',
                            { withCredentials: true }
                        );

                        if (subsResponse.data.success) {
                            setSubscriptions(subsResponse.data.subscriptions || []);
                            setAutoProvisionRequest(response.data.request);
                            setShowSubscriptionModal(true);
                            console.log('Showing subscription modal for auto-provision');
                        } else {
                            throw new Error('Failed to fetch subscriptions');
                        }
                    } else {
                        throw new Error('Failed to fetch auto-provision request');
                    }
                } catch (fetchError) {
                    console.error('Error fetching request for auto-provision:', fetchError);
                    setSubmitError('Failed to load request details. Please try again.');
                    setShowErrorModal(true);
                }
            } else if (result.success) {
                // If no requestId, this is an error for auto-provision flow
                console.error('Auto-provision request submitted but no requestId returned');
                setSubmitError('Request submitted but failed to get request ID. Please check "My Submitted Requests" to approve manually.');
                setShowErrorModal(true);
            } else {
                setSubmitError(result.error || 'Failed to submit request');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Submit error (auto provision confirm):', error);
            setSubmitError(error.message || 'An unexpected error occurred');
            setShowErrorModal(true);
        } finally {
            setSubmitting(false);
            setPendingAutoProvisionData(null);
        }
    };

    const handleSubscriptionConfirm = async (subscriptionId, tenantId, clientId = null, provider = 'azure', linodeAccount = null, selectedTenant = null, imageVersion = 'old') => {
        if (!autoProvisionRequest) return;

        try {
            setShowSubscriptionModal(false);
            setShowLogsModal(true);
            setCreationLogs([]);
            setIsCancelled(false);
            setCreationLogs(prev => [...prev, 'Starting VM creation process...']);

            if (subscriptionId) {
                setCreationLogs(prev => [...prev, `Using subscription ID: ${subscriptionId}`]);
            }

            if (tenantId) {
                setCreationLogs(prev => [...prev, `Using tenant ID: ${tenantId}`]);
            }

            if (selectedTenant) {
                setCreationLogs(prev => [...prev, `Using tenant choice: ${selectedTenant}`]);
            }

            const controller = new AbortController();
            const signal = controller.signal;
            setAbortController(controller);

            const heartbeatInterval = setInterval(() => {
                setCreationLogs(prev => [...prev, `Still processing... (${new Date().toLocaleTimeString()})`]);
            }, 60000);

            try {
                const endpoint = provider === 'linode' ? '/api/approve-linode' : '/api/approve';
                const body = provider === 'linode' 
                    ? {
                        requestId: autoProvisionRequest.id,
                        approverEmail: userEmail,
                        linodeAccount: linodeAccount,
                        imageVersion: imageVersion,
                        diskConfiguration: {
                            dataDiskSizeGB: autoProvisionRequest.formData.dataDiskSizeGB || 1000,
                            createDataDisk: true,
                            setupSwap: true
                        }
                    }
                    : {
                        requestId: autoProvisionRequest.id,
                        approverEmail: userEmail,
                        subscriptionId: subscriptionId,
                        tenantId: tenantId,
                        clientId: clientId,
                        selectedTenant: selectedTenant,
                        imageVersion: imageVersion
                    };

                const response = await fetch(`https://qa-tenantbridge.parablu.com${endpoint}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Cache-Control': 'no-cache',
                        'Connection': 'keep-alive',
                        'Keep-Alive': 'timeout=600'
                    },
                    body: JSON.stringify(body),
                    credentials: 'include',
                    signal,
                    keepalive: true
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    try {
                        const { value, done } = await reader.read();

                        if (done) {
                            console.log('Stream completed normally');
                            clearInterval(heartbeatInterval);
                            break;
                        }

                        const chunk = decoder.decode(value, { stream: true });
                        buffer += chunk;

                        const lines = buffer.split('\n');
                        buffer = lines.pop() || '';

                        for (const line of lines) {
                            if (line.trim()) {
                                try {
                                    const data = JSON.parse(line);

                                    if (data.type === 'log') {
                                        setCreationLogs(prev => [...prev, `${data.message}`]);
                                    } else if (data.type === 'heartbeat') {
                                        console.log('Heartbeat received:', data.message);
                                    } else if (data.type === 'error') {
                                        setVMCreationError({
                                            errorLocation: data.errorLocation || 'VM Creation/Setup',
                                            error: data.message,
                                            requestId: data.requestId || autoProvisionRequest.id,
                                            cloudName: data.cloudName || autoProvisionRequest.formData?.cloudName
                                        });
                                        setShowVMCreationErrorModal(true);
                                        setCreationLogs(prev => [...prev, `❌ Error: ${data.message}`]);
                                    }
                                } catch (parseError) {
                                    console.warn('Failed to parse log line:', line, parseError);
                                }
                            }
                        }
                    } catch (readError) {
                        if (readError.name === 'AbortError') {
                            console.log('Stream aborted by user');
                            clearInterval(heartbeatInterval);
                            break;
                        }
                        throw readError;
                    }
                }

                // Reset form after successful completion
                setFormData({
                    tenant: '4',
                    location: '',
                    vmSize: '2',
                    cloudName: '',
                    storageDestination: '',
                    category: '',
                    diskSizeGB: '512',
                    azureBlobHosted: false,
                    azureBlobFuse: true,
                    hasLicenseFile: false,
                    licenseFile: null,
                    tenantInfo: null,
                    isMultiTenancySetup: false
                });

                setAutoProvisionRequest(null);

            } catch (error) {
                clearInterval(heartbeatInterval);
                if (error.name !== 'AbortError') {
                    console.error('Error processing auto-provision approval:', error);
                    setVMCreationError({
                        errorLocation: 'VM Creation/Setup',
                        error: error.message,
                        requestId: autoProvisionRequest.id,
                        cloudName: autoProvisionRequest.formData?.cloudName
                    });
                    setShowVMCreationErrorModal(true);
                }
            } finally {
                setAbortController(null);
            }

        } catch (error) {
            console.error('Error processing auto-provision approval:', error);
            setSubmitError(error.message || 'Failed to process approval');
            setShowErrorModal(true);
        }
    };

    const handleCancelOperation = () => {
        if (abortController) {
            abortController.abort();
            setCreationLogs(prev => [...prev, `Operation cancelled by ${userEmail}`]);
            setIsCancelled(true);
        }
    };

    const handleClear = () => {
        setFormData({
            tenant: '1', // Default to *.parablu.com
            location: '',
            vmSize: '2',
            cloudName: '',
            storageDestination: '',
            category: '',
            diskSizeGB: '512',
            azureBlobHosted: false,
            azureBlobFuse: true,
            hasLicenseFile: true,
            licenseFile: null,
            tenantInfo: null,
            isMultiTenancySetup: '', // Reset to default
            isMigrationSetup: false
        });
        setErrors({});
        setPortalUrl('');
    };

    useEffect(() => {
        const fetchUserRequests = async () => {
            try {
                const response = await axios.get('https://qa-tenantbridge.parablu.com/api/requests', {
                    withCredentials: true
                });
                setAllRequests(response.data.requests);
                setRequestError(null);
            } catch (error) {
                console.error('Error fetching requests:', error);
                setRequestError('Failed to load your requests');
            }
        };

        fetchUserRequests();

        // Initialize available locations based on default tenant
        // setAvailableLocations(allLocations);
    }, []);

    // Initialize form data from initialValues if provided (for edit mode)
    useEffect(() => {
        if (initialValues) {
            isInitializingFromInitialValues.current = true;
            const resolvedCategory = resolveCategoryValue(initialValues.category);
            const mappedData = {
                tenant: initialValues.tenant || '4',
                location: initialValues.location || '',
                vmSize: initialValues.vmSize || '2',
                cloudName: initialValues.cloudName || '',
                storageDestination: initialValues.storageDestination || '',
                category: resolvedCategory,
                diskSizeGB: initialValues.diskSizeGB || '512',
                azureBlobHosted: initialValues.azureBlobHosted || false,
                azureBlobFuse: initialValues.azureBlobFuse !== undefined ? initialValues.azureBlobFuse : true,
                hasLicenseFile: initialValues.hasLicenseFile || false,
                licenseFile: initialValues.licenseFile || null,
                tenantInfo: initialValues.tenantInfo || null,
                isMultiTenancySetup: initialValues.isMultiTenancySetup !== undefined ? initialValues.isMultiTenancySetup : '',
                isMigrationSetup: initialValues.isMigrationSetup || false
            };
            setFormData(mappedData);

            // Helper function to filter locations based on category
            const filterByCategory = (locations, category) => {
                if (!category) return locations;

                return locations.filter(loc => {
                    // If location has category restrictions, check if current category is allowed
                    if (loc.categories && loc.categories.length > 0) {
                        return loc.categories.includes(category);
                    }
                    // If no category restrictions, location is available for all categories
                    return true;
                });
            };

            // Update available locations based on tenant and category
            const tenant = initialValues.tenant || '4';
            const category = resolvedCategory;
            if (tenant === '4') {
                const locs = allLocations.filter(loc => loc.text !== 'South East Asia');
                setAvailableLocations(filterByCategory(locs, category));
            } else if (tenant === '1') {
                const locs = allLocations.filter(loc => loc.text !== 'South East Asia');
                setAvailableLocations(filterByCategory(locs, category));
            } else if (tenant === '2') {
                const locs = allLocations.filter(loc => loc.text === 'East US' || loc.text === 'South East Asia');
                setAvailableLocations(filterByCategory(locs, category));
            } else if (tenant === '3') {
                const locs = allLocations.filter(loc => loc.text === 'East US');
                setAvailableLocations(filterByCategory(locs, category));
            } else {
                setAvailableLocations(filterByCategory(allLocations, category));
            }
            // Reset the flag after initialization is complete
            setTimeout(() => {
                isInitializingFromInitialValues.current = false;
            }, 0);
        } else {
            // Reset flag when initialValues is cleared
            isInitializingFromInitialValues.current = false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues]);

    return (
        <>
            <Form onSubmit={handleSubmit} error={!!errors.submit}>
                {hasAutoProvisionForSelectedCategory && (
                    <Message info>
                        <Message.Header>Auto Provisioning Enabled</Message.Header>
                        <p>
                            Auto provisioning is enabled for this category. Provisioning will begin automatically after you submit the form.
                        </p>
                    </Message>
                )}
                <Form.Select
                    required
                    label="Category"
                    name="category"
                    placeholder="Select category"
                    options={categoryOptions}
                    value={formData.category}
                    onChange={handleInputChange}
                    error={errors.category ? { content: errors.category } : null}
                />

                <Form.Select
                    required
                    label="Tenant"
                    name="tenant"
                    placeholder="Select tenant"
                    options={tenantOptions}
                    value={formData.tenant}
                    onChange={handleInputChange}
                    error={errors.tenant ? { content: errors.tenant } : null}
                />

                <Form.Select
                    required
                    label="Hosting Location"
                    name="location"
                    placeholder="Select location"
                    options={availableLocations}
                    value={formData.location}
                    onChange={handleInputChange}
                    error={errors.location ? { content: errors.location } : null}
                />

                {/* Hosting Type Selection */}
                <Form.Field required>
                    <label>Hosting Type</label>
                    <Form.Select
                        required // Add required prop
                        name="isMultiTenancySetup"
                        placeholder="Choose hosting type"
                        options={hostingTypeOptions}
                        value={formData.isMultiTenancySetup}
                        onChange={handleInputChange}
                        error={errors.isMultiTenancySetup ? { content: errors.isMultiTenancySetup } : null}
                    />
                </Form.Field>

                {/* Migration Setup Section */}
                <Form.Field>
                    <label>Is this setup being created for migrating single tenancy to v2.6.x?</label>
                    <Form.Radio
                        toggle
                        name="isMigrationSetup"
                        label={formData.isMigrationSetup ? 'Yes' : 'No'}
                        checked={formData.isMigrationSetup}
                        onChange={(e, { checked }) => setFormData(prev => ({
                            ...prev,
                            isMigrationSetup: checked,
                            azureBlobFuse: checked ? false : true,
                            azureBlobHosted: checked ? false : prev.azureBlobHosted
                        }))}
                    />
                </Form.Field>

                <Form.Field required>
                    <label>Decide your URL</label>
                    <div className="ui action input">
                        <Input
                            type="text"
                            style={{ width: '100%' }}
                            label={getTenantUrlPlaceholder()}
                            labelPosition="left"
                            value={formData.cloudName.toLowerCase()}
                            onChange={(e) => handleInputChange(e, { name: 'cloudName', value: e.target.value })}
                            error={!!errors.cloudName}
                            disabled={!!initialValues}
                            placeholder="Enter cloud name"
                        // action={{
                        //     content: '/portal/',
                        //     basic: true
                        // }}
                        />
                    </div>
                    {errors.cloudName && (
                        <div className="ui pointing red basic label">{errors.cloudName}</div>
                    )}
                </Form.Field>

                {/* Portal URL Display Section */}
                {portalUrl && (
                    <Message info size="small" style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
                        <Message.Header style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span>Portal URL</span>
                            <Icon
                                name={copySuccess ? "check" : "copy"}
                                link
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(portalUrl);
                                        setCopySuccess(true);
                                        setTimeout(() => {
                                            setCopySuccess(false);
                                        }, 2000);
                                    } catch (err) {
                                        console.error('Failed to copy:', err);
                                        // Fallback for older browsers
                                        const textArea = document.createElement('textarea');
                                        textArea.value = portalUrl;
                                        textArea.style.position = 'fixed';
                                        textArea.style.opacity = '0';
                                        document.body.appendChild(textArea);
                                        textArea.select();
                                        try {
                                            document.execCommand('copy');
                                            setCopySuccess(true);
                                            setTimeout(() => {
                                                setCopySuccess(false);
                                            }, 2000);
                                        } catch (fallbackErr) {
                                            console.error('Fallback copy failed:', fallbackErr);
                                        }
                                        document.body.removeChild(textArea);
                                    }
                                }}
                                style={{ cursor: 'pointer', color: copySuccess ? '#6F9911' : undefined }}
                                title={copySuccess ? "Copied!" : "Copy to clipboard"}
                            />
                        </Message.Header>
                        <p style={{ wordBreak: 'break-all', fontSize: '0.9rem' }}>
                            <Icon name="linkify" /> {portalUrl}
                        </p>
                    </Message>
                )}

                {/* Copy Success Toast Notification */}
                {copySuccess && (
                    <div style={{
                        position: 'fixed',
                        right: '16px',
                        top: '16px',
                        zIndex: 1000,
                        animation: 'slideInDown 0.3s ease-out'
                    }}>
                        <Message positive size="small" style={{
                            minWidth: '280px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            borderRadius: '8px'
                        }}>
                            <Icon name="check circle" /> Portal URL copied to clipboard!
                        </Message>
                    </div>
                )}

                {!formData.isMigrationSetup && (
                    <Form.Select
                        required
                        label="Storage Destination"
                        name="storageDestination"
                        placeholder="Select storage destination"
                        options={storageOptions}
                        value={formData.storageDestination}
                        onChange={handleInputChange}
                        error={errors.storageDestination ? { content: errors.storageDestination } : null}
                    />
                )}


                <div className="button-container">
                    <Button type="button" onClick={handleClear} disabled={submitting}>
                        Clear
                    </Button>
                    <Button type="submit" primary loading={submitting} disabled={submitting}>
                        Submit
                    </Button>
                </div>
            </Form>

            {/* Auto Provisioning Verification Modal */}
            {hasAutoProvisionForSelectedCategory && (
                <Modal
                    open={showAutoProvisionVerifyModal}
                    onClose={() => {
                        if (!submitting) {
                            setShowAutoProvisionVerifyModal(false);
                            setPendingAutoProvisionData(null);
                        }
                    }}
                    size="small"
                    closeOnDimmerClick={!submitting}
                >
                    <Modal.Header>
                        Verify Details Before Auto Provisioning
                    </Modal.Header>
                    <Modal.Content>
                        <Message info>
                            <Message.Header>Review your request</Message.Header>
                            <p>
                                You have Auto Provisioning enabled for this category. Please verify the key details
                                below. After you confirm, your request will be submitted and can be auto-approved
                                by you without any additional approver.
                            </p>
                        </Message>
                        <div style={{ marginTop: '1rem' }}>
                            <p><strong>Category:</strong> {formData.category || 'N/A'}</p>
                            <p><strong>Tenant:</strong> {tenantOptions.find(t => t.value === formData.tenant)?.text || 'N/A'}</p>
                            <p><strong>Location:</strong> {availableLocations.find(l => l.value === formData.location)?.text || 'N/A'}</p>
                            <p><strong>Cloud Name:</strong> {formData.cloudName?.toLowerCase() || 'N/A'}</p>
                            {portalUrl && (
                                <p><strong>Portal URL:</strong> {portalUrl}</p>
                            )}
                        </div>
                    </Modal.Content>
                    <Modal.Actions>
                        <Button
                            onClick={() => {
                                if (!submitting) {
                                    setShowAutoProvisionVerifyModal(false);
                                    setPendingAutoProvisionData(null);
                                }
                            }}
                            disabled={submitting}
                        >
                            Back to Form
                        </Button>
                        <Button
                            primary
                            onClick={handleConfirmAutoProvisionSubmit}
                            loading={submitting}
                            disabled={submitting}
                        >
                            Confirm & Submit
                        </Button>
                    </Modal.Actions>
                </Modal>
            )}

            <SuccessModal
                open={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                roles={roles}
                userEmail={userEmail}
                categories={categories}
                requestCategory={formCategory}
            />

            <ErrorModal
                open={showErrorModal}
                onClose={() => setShowErrorModal(false)}
                error={submitError}
            />

            <SubscriptionSelectionModal
                open={showSubscriptionModal}
                onClose={() => {
                    setShowSubscriptionModal(false);
                    setAutoProvisionRequest(null);
                }}
                onConfirm={handleSubscriptionConfirm}
                subscriptions={subscriptions}
                requestData={autoProvisionRequest}
                linodeEnabled={linodeEnabled}
            />

            <CreationLogsModal
                open={showLogsModal}
                onClose={() => {
                    // For completed operations, just close the modal.
                    // Do NOT clear logs here – they are reset explicitly when a new
                    // provisioning run starts so that the final elapsed time and
                    // completion status remain visible if the user re-opens the modal.
                    setShowLogsModal(false);
                }}
                onCancel={handleCancelOperation}
                isCancelled={isCancelled}
                logs={creationLogs}
            />

            <VMCreationErrorModal
                open={showVMCreationErrorModal}
                onClose={() => {
                    setShowVMCreationErrorModal(false);
                    setVMCreationError(null);
                }}
                {...vmCreationError}
            />
        </>
    );
};

export default AzureForm;